import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Spin, Alert, Modal, Table, Tag, Segmented, Empty } from "antd";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import customClientAxios from "../../customAxios";
import {
  PlatformAnalyticsInstaPlanStats,
  PlatformAnalyticsInstaPlanDrilldown,
} from "../../utils/ApiEndpoints";
import {
  PageContainer, Card, SectionTitle, StatsGrid, StatCard,
  StatLabel, StatValue, StatSub, fmtNum,
} from "../paUtils";
import styled from "styled-components";

// ── Plan visual config ────────────────────────────────────────────────────

const PLAN_TYPE_META = {
  free:  { label: "Free",       color: "#94a3b8", description: "1500 free credits — default on Instagram connection" },
  pro:   { label: "Pro (Paid)", color: "#6366f1", description: "Paid Instagram automation — packs and unlimited" },
};

// Fallback for any planType the API surfaces beyond the known ones
const fallbackColor = (key) => {
  const palette = ["#10b981", "#f59e0b", "#ec4899", "#14b8a6", "#a855f7", "#3b82f6"];
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return palette[hash % palette.length];
};

const planMeta = (key) =>
  PLAN_TYPE_META[key] || {
    label: key.charAt(0).toUpperCase() + key.slice(1),
    color: fallbackColor(key),
    description: null,
  };

// ── Styled ────────────────────────────────────────────────────────────────

const ClickableStatCard = styled(StatCard)`
  cursor: pointer;
  border-left: 4px solid ${(p) => p.$color || "#cbd5e1"};
  &:hover { border-color: ${(p) => p.$color || "#cbd5e1"}; }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
`;

const TooltipBox = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px 16px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  font-size: 13px;
`;

const BreakdownGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
`;

const BreakdownPill = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px 16px;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover {
    border-color: ${(p) => p.$color || "#cbd5e1"};
    background: white;
    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
  }
`;

const PillLabel = styled.div`
  font-size: 11px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
`;

const PillValue = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: ${(p) => p.$color || "#1e293b"};
`;

const PillSub = styled.div`
  font-size: 11px;
  color: #94a3b8;
  margin-top: 4px;
`;

// ── Helpers ───────────────────────────────────────────────────────────────

const fmtDate = (v) =>
  v
    ? new Date(v).toLocaleDateString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
      })
    : "—";

const fmtCredits = (n) => {
  if (n === -1) return "Unlimited";
  if (n === undefined || n === null) return "—";
  return fmtNum(n);
};

// ── Tooltip ───────────────────────────────────────────────────────────────

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <TooltipBox>
      <div style={{ fontWeight: 600, color: "#1e293b", marginBottom: 6 }}>{label}</div>
      {payload.map((entry) => (
        <div
          key={entry.dataKey}
          style={{
            display: "flex", alignItems: "center", gap: 8,
            margin: "4px 0", color: "#374151",
          }}
        >
          <span style={{
            display: "inline-block", width: 8, height: 8,
            borderRadius: "50%", background: entry.color,
          }} />
          <span style={{ color: "#64748b", minWidth: 100, textTransform: "capitalize" }}>
            {entry.name}:
          </span>
          <span style={{ fontWeight: 600 }}>{fmtNum(entry.value)}</span>
        </div>
      ))}
    </TooltipBox>
  );
}

// ── Drilldown Modal ───────────────────────────────────────────────────────

const DRILL_COLUMNS = [
  {
    title: "Owner",
    key: "owner",
    render: (_, r) => (
      <div>
        <div style={{ fontWeight: 600, color: "#1e293b", fontSize: 13 }}>
          {r.ownerName || "—"}
        </div>
        <div style={{ fontSize: 11, color: "#94a3b8" }}>{r.ownerEmail || ""}</div>
      </div>
    ),
  },
  {
    title: "Brand Page",
    key: "bp",
    render: (_, r) => (
      <div>
        <div style={{ fontWeight: 500, color: "#1e293b", fontSize: 13 }}>{r.brandPageName || "—"}</div>
        {r.brandPageSlug && <div style={{ fontSize: 11, color: "#94a3b8" }}>/{r.brandPageSlug}</div>}
      </div>
    ),
  },
  {
    title: "IG Handle",
    dataIndex: "igUsername",
    key: "igUsername",
    width: 140,
    render: (v) => (v ? <span style={{ fontSize: 13 }}>@{v}</span> : "—"),
  },
  {
    title: "Phone",
    dataIndex: "ownerPhone",
    key: "ownerPhone",
    width: 130,
    render: (v) => v || "—",
  },
  {
    title: "Billing",
    dataIndex: "billingCycle",
    key: "billingCycle",
    width: 110,
    render: (v) => (v ? <Tag style={{ fontSize: 11 }}>{v}</Tag> : <Tag>—</Tag>),
  },
  {
    title: "Credits",
    key: "credits",
    width: 120,
    render: (_, r) => (
      <div>
        <div style={{ fontSize: 13, fontWeight: 500 }}>
          {fmtCredits(r.totalCreditsRemaining)}
        </div>
        {r.totalCredits > 0 && r.totalCreditsRemaining !== -1 && (
          <div style={{ fontSize: 11, color: "#94a3b8" }}>
            of {fmtCredits(r.totalCredits)}
          </div>
        )}
      </div>
    ),
  },
  {
    title: "Plan Started",
    dataIndex: "planStart",
    key: "planStart",
    width: 130,
    render: fmtDate,
  },
  {
    title: "Plan Ends",
    dataIndex: "planEndDate",
    key: "planEndDate",
    width: 130,
    render: fmtDate,
  },
];

function DrilldownModal({ open, planType, startDate, endDate, onClose }) {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const meta = planType ? planMeta(planType) : null;

  const fetchRows = useCallback(async () => {
    if (!planType || !open) return;
    setLoading(true);
    setErr(null);
    try {
      const res = await customClientAxios().get(PlatformAnalyticsInstaPlanDrilldown, {
        params: { planType, startDate, endDate, page, limit: 50 },
      });
      setRows(res.data.data.rows || []);
      setTotal(res.data.data.pagination?.total || 0);
    } catch (e) {
      setErr(e?.response?.data?.error || "Failed to load drilldown");
    } finally {
      setLoading(false);
    }
  }, [planType, open, startDate, endDate, page]);

  useEffect(() => { fetchRows(); }, [fetchRows]);
  useEffect(() => { setPage(1); }, [planType, startDate, endDate]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={1200}
      title={
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{
              display: "inline-block", width: 12, height: 12,
              borderRadius: "50%", background: meta?.color || "#cbd5e1",
            }} />
            <span style={{ fontWeight: 600 }}>
              {meta?.label || planType} — Instagram Automation Clients
            </span>
            <Tag>{total} clients</Tag>
          </div>
          {meta?.description && (
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
              {meta.description}
            </div>
          )}
        </div>
      }
    >
      {err && <Alert type="error" message={err} style={{ marginBottom: 12 }} />}
      <Spin spinning={loading}>
        <Table
          dataSource={rows}
          columns={DRILL_COLUMNS}
          rowKey="brandPageId"
          size="middle"
          pagination={{
            current: page,
            pageSize: 50,
            total,
            onChange: setPage,
            showSizeChanger: false,
            showTotal: (t) => `${t} clients`,
          }}
          locale={{ emptyText: <Empty description="No clients on this plan in the selected range" /> }}
          scroll={{ x: 1100 }}
        />
      </Spin>
    </Modal>
  );
}

// ── Main Tab ──────────────────────────────────────────────────────────────

function InstaPlansTab({ startDate, endDate }) {
  const [period, setPeriod] = useState("daily");
  const [data, setData] = useState({
    totalsByPlanType: {},
    breakdown: [],
    planKeys: [],
    timeSeries: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [drillPlan, setDrillPlan] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await customClientAxios().get(PlatformAnalyticsInstaPlanStats, {
        params: { startDate, endDate, period },
      });
      setData(res.data.data || {
        totalsByPlanType: {}, breakdown: [], planKeys: [], timeSeries: [],
      });
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to load Instagram plan stats");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totals = data.totalsByPlanType || {};
  const breakdown = data.breakdown || [];
  const total = Object.values(totals).reduce((a, b) => a + b, 0);

  // Ensure stable order — known plans first, then any extras
  const planKeysSorted = useMemo(() => {
    const known = ["free", "pro"];
    const extras = Object.keys(totals).filter((k) => !known.includes(k));
    return [...known.filter((k) => totals[k] !== undefined), ...extras];
  }, [totals]);

  // Chart bars: union of known keys + planKeys from API
  const chartKeys = useMemo(() => {
    const set = new Set([...(data.planKeys || []), ...planKeysSorted]);
    return Array.from(set);
  }, [data.planKeys, planKeysSorted]);

  return (
    <PageContainer>
      {error && (
        <Alert type="error" message={error} style={{ marginBottom: 20 }}
               closable onClose={() => setError(null)} />
      )}

      <Spin spinning={loading}>
        {/* Top stat cards (planType-level) */}
        <StatsGrid>
          {planKeysSorted.map((key) => {
            const meta = planMeta(key);
            const count = totals[key] || 0;
            const pct = total > 0 ? ((count / total) * 100).toFixed(1) : "0.0";
            return (
              <ClickableStatCard
                key={key}
                $color={meta.color}
                onClick={() => setDrillPlan(key)}
              >
                <StatLabel style={{ color: meta.color }}>{meta.label}</StatLabel>
                <StatValue>{fmtNum(count)}</StatValue>
                <StatSub>{pct}% of insta clients · click to view</StatSub>
              </ClickableStatCard>
            );
          })}
          {planKeysSorted.length === 0 && (
            <StatCard>
              <StatLabel>No plans yet</StatLabel>
              <StatValue>0</StatValue>
              <StatSub>No clients have an automation plan in this range</StatSub>
            </StatCard>
          )}
        </StatsGrid>

        {/* Sub-breakdown by billing cycle */}
        {breakdown.length > 0 && (
          <Card>
            <SectionTitle>Breakdown by Billing Cycle</SectionTitle>
            <BreakdownGrid>
              {breakdown.map((b, i) => {
                const meta = planMeta(b.planType);
                return (
                  <BreakdownPill
                    key={`${b.planType}-${b.billingCycle}-${i}`}
                    $color={meta.color}
                    onClick={() => setDrillPlan(b.planType)}
                  >
                    <PillLabel>
                      {meta.label} ·{" "}
                      <span style={{ textTransform: "lowercase" }}>
                        {b.billingCycle === "none" ? "no cycle" : b.billingCycle}
                      </span>
                    </PillLabel>
                    <PillValue $color={meta.color}>{fmtNum(b.count)}</PillValue>
                    <PillSub>clients</PillSub>
                  </BreakdownPill>
                );
              })}
            </BreakdownGrid>
          </Card>
        )}

        {/* Time-series chart */}
        <Card>
          <HeaderRow>
            <SectionTitle style={{ margin: 0 }}>
              Insta Plan Adoption Over Time
            </SectionTitle>
            <Segmented
              value={period}
              onChange={setPeriod}
              options={[
                { label: "Daily",   value: "daily" },
                { label: "Weekly",  value: "weekly" },
                { label: "Monthly", value: "monthly" },
              ]}
            />
          </HeaderRow>

          {data.timeSeries?.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={data.timeSeries} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: "#94a3b8" }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#94a3b8" }}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {chartKeys.map((key) => (
                  <Bar
                    key={key}
                    dataKey={key}
                    name={planMeta(key).label}
                    stackId="plans"
                    fill={planMeta(key).color}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Empty description="No data for the selected range" />
          )}
        </Card>
      </Spin>

      <DrilldownModal
        open={!!drillPlan}
        planType={drillPlan}
        startDate={startDate}
        endDate={endDate}
        onClose={() => setDrillPlan(null)}
      />
    </PageContainer>
  );
}

export default InstaPlansTab;
